import { Component, OnInit, ViewChild } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import { AuthService } from "app/auth/auth.service";
import { ROLE_LOOKUP_ABBREVIATION } from "configs/calendar-config";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import AssessmentRole from "models/assessment/assessment-role";
import UnassignedOrder from "models/calendar/unassigned-order";
import LevelOfCare, {
  LevelOfCareResponse,
} from "models/patient-chart/levelOfCare";
import * as moment from "moment-timezone";
import VisitTypes from "pages/calendar/visit-type";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { BenefitPeriod } from "../../certification/models/cti";
import { CertificationService } from "../../certification/services/certification-service";
import CompanyRole from "../../models/company/company-role";
import { CompanyTeam } from "../../models/company/company-team";
import TeamMember from "../../models/company/team-member";
import PatientContact from "../../models/patient-chart/contacts/patient-contact";
import Diagnosis from "../../models/patient-chart/diagnosis";
import { Patient } from "../../models/patient-chart/patient";
import Credential from "../../models/resource-management/credential";
import { CalendarService } from "../../providers/calendar-service/calendar-service";
import { ResourceManagementService } from "../../providers/resource-management/resource-management.service";

const SCHEDULED = "SCHEDULED";

interface User {
  username: string;
  id: number;
  login: string;
}

@Component({
  selector: "calendar-schedule-order",
  templateUrl: "calendar-schedule-order.html",
})
export class CalendarScheduleOrder implements OnInit {
  // form stuff
  scheduleForm: FormGroup;
  timeFormat: string = "HH:mm";

  // models
  companyTeams: CompanyTeam[];
  companyRoles: CompanyRole[];
  users: User[] = [];
  patient: Patient;
  primaryContact: PatientContact;
  unassignedVisit: UnassignedOrder; //UnassignedVisit;
  levelOfCare: LevelOfCare;
  levelOfCareData: LevelOfCareResponse;
  patientCareType: String = "";
  patientAge: string = "--";
  startTimeValue: any = { hour: 8, minute: 0 };
  endTimeValue: any = { hour: 8, minute: 30 };
  days: string[] = ["SUN", "MON", "TUES", "WED", "THUR", "FRI", "SAT"];

  spinnerCounter: number = 0;
  spinner: any = null;
  visitTypes: VisitTypes[] = [];
  assessmentRoles: AssessmentRole[];
  daysControlSub: Subscription;
  daysBeforeStartDate: boolean = false;
  credentialsTypes: Credential[] = [];
  showCredentialsWarning: string = "";
  errorMessage: any = {
    resourceName: "",
    credential: "",
    expireDate: "",
  };

  disableSaveButton: boolean = false;
  currentBenefitPeriod: BenefitPeriod = {
    benefitPeriod: 0,
    startDate: "",
    endDate: "",
  } as BenefitPeriod;
  nextBenefitPeriod: BenefitPeriod = {
    benefitPeriod: 0,
    startDate: "",
    endDate: "",
  } as BenefitPeriod;

  @ViewChild("resourceListSelect") resourceListSelect: NgSelectComponent;
  @ViewChild("resourceHiddenInput") resourceHiddenInput: {
    setFocus: () => void;
  };

  constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    private calendarService: CalendarService,
    private navParams: NavParams,
    private viewCtrl: ViewController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private referralService: ReferralsServiceProvider,
    private resourceManagementService: ResourceManagementService,
    private authSvc: AuthService,
    private certificationSvc: CertificationService
  ) {
    this.unassignedVisit = this.navParams.data.unscheduledOrder;
    this.patient = this.navParams.data.patient;
    this.companyTeams = this.navParams.data.teams;
    this.primaryContact = this.navParams.data.primaryContact;
    this.companyRoles = this.navParams.data.roles;
    if (this.companyRoles.length === 1) {
      this.unassignedVisit.role = this.companyRoles[0].display;
    }

    this.resourceManagementService.getLookUpValues().subscribe((value) => {
      this.credentialsTypes = value["CredentialType"];
    });
    let daysArray = new FormArray([], this.correctWeeksSelected());
    for (let i = 0; i < this.days.length; i++) {
      let fg = this.fb.group({
        day: this.fb.control(this.days[i]),
        selected: this.fb.control(false),
      });
      daysArray.push(fg);
    }

    this.scheduleForm = this.fb.group(
      {
        companyTeam: this.fb.control("", [Validators.required]),
        role: this.fb.control(this.unassignedVisit.role, [Validators.required]),
        disciplineCode: this.fb.control(this.unassignedVisit.disciplineCode),
        assignedTo: this.fb.control("", [Validators.required]),
        days: daysArray,
        startDate: this.fb.control(this.unassignedVisit.orderStartDate, [
          Validators.required,
        ]),
        discontinueDate: this.fb.control(
          this.unassignedVisit.orderDiscontinueDate
        ),
        startTime: this.fb.control(this.startTimeValue, [Validators.required]),
        endTime: this.fb.control(this.endTimeValue, [Validators.required]),
        activityNote: this.fb.control(this.getActivityNote()),
        visitType: this.fb.control(this.unassignedVisit.visitType, [
          Validators.required,
        ]),
        visitTypeName: this.fb.control(this.unassignedVisit.visitTypeName, [
          Validators.required,
        ]),
      },
      { validator: this.timesAreAligned() }
    );

    if (this.unassignedVisit.startTime !== "") {
      const startValue = {
        hour: this.unassignedVisit.startTime.split(":")[0],
        minute: this.unassignedVisit.startTime.split(":")[1],
      };
      let localTime = moment(startValue).local();
      this.startTimeValue = {
        hour: localTime.hour(),
        minute: localTime.minute(),
      };
    }

    if (this.unassignedVisit.endTime !== "") {
      const endValue = {
        hour: this.unassignedVisit.endTime.split(":")[0],
        minute: this.unassignedVisit.endTime.split(":")[1],
      };
      let localTime = moment(endValue).local();
      this.endTimeValue = {
        hour: localTime.hour(),
        minute: localTime.minute(),
      };
    }

    if (this.unassignedVisit.days) {
      this.unassignedVisit.days.forEach((day) => {
        var newVal = this.scheduleForm.get("days").get(day.toString()).value;
        newVal.selected = true;
        this.scheduleForm.get("days").get(day.toString()).setValue(newVal);
      });
    }

    //Team disabled temporary
    this.scheduleForm.controls["companyTeam"].disable();

    this.companyTeams.unshift({
      createdBy: undefined,
      createdDatetime: undefined,
      id: 0,
      name: "All",
      patientCount: 0,
      qbosId: 0,
      updatedBy: undefined,
      updatedDatetime: undefined,
    });

    this.setupCareTeamSearch();
    this.scheduleForm.controls["companyTeam"].setValue(this.careTeamId);
  }

  getActivityNote(): string {
    return this.unassignedVisit.activityNote
      ? this.unassignedVisit.activityNote
      : this.unassignedVisit.providerNotes
      ? `Provider Notes: ${this.unassignedVisit.providerNotes}`
      : "";
  }

  get daysControl(): FormArray {
    return this.scheduleForm.get("days") as FormArray;
  }

  /**
   * Handles the control initialization
   */
  ngOnInit(): void {
    this.getLevelOfCare();
    this.calculatePatientAge();
    if (this.patient && this.patient.careType) {
      this.patientCareType = this.patient.careType.toLowerCase();
      this.initializeUpcomingBenefits();
    }

    //This method can be deleted when the new description is completed
    this.loadDescription();
    this.setDaysBeforeStartDate();
    this.scheduleForm.controls["assignedTo"].valueChanges.subscribe((value) => {
      this.checkCredentials(value);
    });
  }

  checkCredentials(patientId: number) {
    const formType = "scheduleForm";
    const assignedToId = this.scheduleForm.controls["assignedTo"].value;
    const formControls = this.scheduleForm.controls;
    this.showCredentialsWarning = "";

    this.resourceManagementService
      .getCredentialsByPatientId(
        patientId,
        formType,
        assignedToId,
        this.users,
        this.credentialsTypes,
        this.companyRoles,
        formControls,
        this.unassignedVisit
      )
      .subscribe(({ response, disableSaveButton }) => {
        this.showCredentialsWarning = response;
        this.disableSaveButton = disableSaveButton;
      });
  }

  loadDescription() {
    if (
      this.unassignedVisit.orderDescription &&
      this.unassignedVisit.orderDescription.split("X").length === 3
    ) {
      let description = this.unassignedVisit.orderDescription.split("X");
      let duration: number = +this.unassignedVisit.duration;
      let order: string = description[0]
        .trim()
        .replace(new RegExp("\\bSkilled Nursing\\b"), "Skilled Nurse")
        .replace(new RegExp("\\bHospice Aid\\b"), "Hospice Aide");
      const visits: number = +description[1].trim();
      const interval: string = description[2].trim();
      const total: number = visits * duration;

      if (visits === 1)
        this.unassignedVisit.orderDescription = `${order}: ${visits} visit per ${interval}`;
      else
        this.unassignedVisit.orderDescription = `${order}: ${visits} visits per ${interval}`;

      if (duration > 0) {
        if (duration === 1)
          this.unassignedVisit.orderDescription += ` for ${duration} ${interval}`;
        else
          this.unassignedVisit.orderDescription += ` for ${duration} ${interval}s`;

        if (total === 1)
          this.unassignedVisit.orderDescription += ` (${
            visits * duration
          } visit)`;
        else
          this.unassignedVisit.orderDescription += ` (${
            visits * duration
          } visits)`;
      }
    }
  }

  calculatePatientAge() {
    if (this.patient && this.patient.birthDate) {
      const timeDiff = Math.abs(
        Date.now() - new Date(this.patient.birthDate).getTime()
      );
      this.patientAge = Math.floor(
        timeDiff / (1000 * 3600 * 24) / 365.25
      ).toString();
    }
  }

  get primaryDiagnosis(): Diagnosis {
    let d = this.patient.diagnosis.filter((x) => x.diagnosisType === "PRIMARY");
    if (d && d.length > 0) return d[0];
    return null;
  }

  setupCareTeamSearch(): void {
    this.scheduleForm.controls["companyTeam"].valueChanges.subscribe((x) => {
      this.searchUsers(x, false);
    });
  }

  /**
   * Searches users by the team
   * @param roleId the id of the role
   */
  searchUsers(newRoleId: number, selectNewUser: boolean) {
    let roleId: number;

    roleId = newRoleId !== 0 ? newRoleId : this.roleId;

    if (roleId) {
      this.calendarService
        .getAllUsersByRole(roleId, true, true)
        .pipe(take(1))
        .subscribe((x) => {
          this.prepUsers(x.content);
          if (selectNewUser) {
            this.scheduleForm.get("role").setValue(this.unassignedVisit.role);
            this.scheduleForm
              .get("assignedTo")
              .setValue(this.unassignedVisit.assignedTo);
          }
        });
    }
  }

  private prepUsers(members: TeamMember[]): void {
    this.users = [];
    for (let i = 0; i < members.length; i++) {
      let o = members[i];
      //SUP-16431: validate by discipline instead of login
      if (o.discipline && o.discipline != "") {
        this.users.push({
          username: o.displayName,
          id: o.qbosId,
          login: o.login,
        });
      }
    }
    this.users = this.users.sort((a, b) =>
      a.username.localeCompare(b.username)
    );
  }

  /*filterUsers(teamMembers: TeamMember[]): TeamMember[] {
    let tm: TeamMember[] = [];
    for (let i = 0; i < teamMembers.length; i ++) {
      if (teamMembers[i].roles.includes(this.unassignedVisit.disciplineCode)) {
        tm.push(teamMembers[i]);
      }
    }

    tm.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return tm;
  }

  get endTime(): FormControl {
    return this.scheduleForm.controls['endTime'] as FormControl;
  }

  get startTime(): FormControl {
    return this.scheduleForm.controls['startTime'] as FormControl;
  }*/

  generatePayload(): UnassignedOrder {
    const user = this.authSvc.getUser();
    let newOrder = this.unassignedVisit as UnassignedOrder;
    let f = new Date();
    let t = new Date();
    f.setHours(this.startTimeValue.hour, this.startTimeValue.minute);
    t.setHours(this.endTimeValue.hour, this.endTimeValue.minute);
    let u = this.users.find(
      (user) => user.id === this.scheduleForm.controls["assignedTo"].value
    );
    if (!u) {
      let message =
        "Error saving calendar event as could not get the assign to!";
      this.calendarService.presentErrorToast(null, message);
      return;
    }
    newOrder.assignedTo = u.id.toString();
    newOrder.assignedToFullName = u.username;
    newOrder.assignedToUserName = u.login;

    newOrder.patientChartId = this.patient.externalId;
    newOrder.patientFirstName = this.patient.firstName;
    newOrder.patientLastName = this.patient.lastName;
    newOrder.patientMiddleInitial = this.patient.middleInitial;

    newOrder.role = this.scheduleForm.controls["role"].value;
    newOrder.disciplineCode =
      this.scheduleForm.controls["disciplineCode"].value;
    newOrder.visitType = this.scheduleForm.controls["visitType"].value;
    newOrder.visitTypeName = this.scheduleForm.controls["visitTypeName"].value;

    newOrder.startTime = moment(f).format(this.timeFormat);
    newOrder.endTime = moment(t).format(this.timeFormat);
    newOrder.timeInterval = newOrder.timeInterval.toUpperCase();
    // newOrder.careType = this.patientCareType.toUpperCase();
    newOrder.careLocation =
      this.levelOfCareData && this.levelOfCareData.careLocationType
        ? this.levelOfCareData.careLocationType.description
        : "";

    newOrder.updatedBy = user.name;
    newOrder.updatedByUserId = user.userId.toString();
    newOrder.updatedByUserName = user.email;
    newOrder.updatedDatetime = moment.utc(moment()).format();
    newOrder.activityNote = this.scheduleForm.controls["activityNote"].value;
    newOrder.status = SCHEDULED;

    newOrder.days = [];
    this.daysControl.controls.forEach((x, y) => {
      let day = x.get("day").value;
      let selected = x.get("selected").value;
      if (selected) {
        newOrder.days.push(this.getDayFromString(day));
      }
    });

    return newOrder;
  }

  getDayFromString(day: string): number {
    switch (day.toLocaleLowerCase()) {
      case "sun":
        return 0;
      case "mon":
        return 1;
      case "tues":
        return 2;
      case "wed":
        return 3;
      case "thur":
        return 4;
      case "fri":
        return 5;
      case "sat":
        return 6;
    }
  }

  // NOVA-1514: warn on Edit Series Save that past unperformed visits
  // are preserved. New-series saves (status UNSCHEDULED) skip the dialog.
  submitForm() {
    const isEditSeries =
      this.unassignedVisit && this.unassignedVisit.status === SCHEDULED;

    if (!isEditSeries) {
      this.performSave();
      return;
    }

    const alert = this.alertCtrl.create({
      title: "Edit Series",
      message:
        "Saving this change will update upcoming visits for this order. " +
        "Any past unperformed visits will remain unchanged on the calendar.",
      buttons: [
        { text: "Cancel", role: "cancel" },
        { text: "Continue", handler: () => this.performSave() },
      ],
    });
    alert.present();
  }

  private performSave() {
    let p = this.generatePayload();

    this.increaseSpinnerCount("Scheduling order ...");
    this.calendarService.saveUnscheduledEvent([p]).subscribe(
      (data: UnassignedOrder) => {
        this.decreaseSpinnerCount();
        this.viewCtrl.dismiss({ unassignedOrder: data });
      },
      (err) => {
        this.decreaseSpinnerCount();
        this.calendarService.presentErrorToast(
          err,
          "Error saving unassigned visit"
        );
      }
    );
  }

  /**
   * Increases the spinner counter for display
   * @param message
   */
  increaseSpinnerCount(message: string = "Loading ...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  /**
   * Decreases the counter so not to show the loader anymore
   */
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  cancel() {
    this.viewCtrl.dismiss({});
  }

  correctWeeksSelected(): ValidatorFn {
    return (formArray: FormArray): { [key: string]: any } | null => {
      let valid: boolean = true;
      if (this.unassignedVisit.timeInterval !== "DAY") {
        let selected = 0;
        formArray.controls.forEach((x: FormGroup) => {
          if (x.controls["selected"].value) {
            selected += 1;
          }
        });

        if (this.unassignedVisit.timeInterval === "MONTH" && selected === 1) {
          valid = true;
        } else if (selected !== this.unassignedVisit.frequency) {
          valid = false;
        }
      }

      return valid ? null : { error: "Wrong number of days selected" };
    };
  }

  get roleId(): number {
    for (let i = 0; i < this.companyRoles.length; i++) {
      let o = this.companyRoles[i];
      if (o.lookup === this.unassignedVisit.disciplineCode) {
        return o.id;
      }
    }
  }

  get careTeamId(): number {
    if (!this.patient.teamName) return null;
    for (let i = 0; i < this.companyTeams.length; i++) {
      if (this.patient.teamName === this.companyTeams[i].name) {
        return this.companyTeams[i].id;
      }
    }
    return null;
  }

  timesAreAligned() {
    return (): { [key: string]: any } => {
      let f = new Date();
      let t = new Date();
      f.setHours(this.startTimeValue.hour, this.startTimeValue.minute);
      t.setHours(this.endTimeValue.hour, this.endTimeValue.minute);
      if (f >= t) {
        return {
          dates: "Start time must be less than end time",
        };
      }

      return {};
    };
  }

  // handleDisciplineChange(role): void {
  handleRoleChange(role, selectNewUser: boolean = false): void {
    if (role && role.lookup && role.id) {
      this.scheduleForm.controls["assignedTo"].setValue("");
      this.scheduleForm.controls["disciplineCode"].setValue(role.lookup);

      // this.scheduleForm.controls['visitType'].setValue('');
      // this.scheduleForm.controls['visitTypeName'].setValue('');

      this.setVisitTypes(role.lookup, selectNewUser);

      let newRole = this.companyRoles.find((c) => {
        return c.id === role.id;
      });
      if (newRole) {
        this.searchUsers(newRole.id, selectNewUser);
      } else {
        this.searchUsers(0, selectNewUser);
      }
    } else {
      this.scheduleForm.controls["role"].reset();
      this.scheduleForm.controls["disciplineCode"].reset();
    }
  }

  onVisitTypeChange(e: any) {
    if (e) {
      this.scheduleForm.controls["visitType"].setValue(e.code);
      this.scheduleForm.controls["visitTypeName"].setValue(e.displayName);
    }
  }

  setVisitTypes(disciplineCode: string, isEditMode: boolean) {
    if (!isEditMode) {
      this.scheduleForm.controls["visitType"].setValue("");
      this.scheduleForm.controls["visitTypeName"].setValue("");
    }
    let visitTypes: VisitTypes[] = [];
    this.assessmentRoles = this.calendarService.initAssessmentRoles(
      this.levelOfCare,
      String(this.patientCareType)
    );
    this.assessmentRoles
      .find((r) => r.lookupCode === ROLE_LOOKUP_ABBREVIATION[disciplineCode])
      .types.forEach((type) => {
        let visitType: VisitTypes = {
          code: type.lookupCode,
          displayName: type.lookupValue,
        };
        visitTypes.push(visitType);
      });
    this.visitTypes = visitTypes && visitTypes.length > 0 ? visitTypes : [];
  }

  getRoleId(roleCode: string): number {
    for (let i = 0; i < this.companyRoles.length; i++) {
      let o = this.companyRoles[i];
      if (o && o.lookup === roleCode) {
        return o.id;
      }
    }
  }

  get roleCode(): string {
    let roleId = this.roleId;
    let lookup = this.companyRoles.find((x) => x.id === roleId);

    for (let i = 0; i < this.companyRoles.length; i++) {
      let o = this.companyRoles[i];
      if (o && o.lookup === lookup.lookup) {
        return o.lookup;
      }
    }
  }

  getLevelOfCare() {
    this.increaseSpinnerCount("Loading...");
    this.getLocData().subscribe(
      (data: LevelOfCareResponse) => {
        if (data) {
          this.levelOfCareData = data;
          this.levelOfCare = Object.assign(this.levelOfCareData);
          if (
            this.levelOfCareData.careLocationType &&
            this.levelOfCareData.careLocationType.description.toLocaleLowerCase() ===
              "home"
          ) {
            this.levelOfCare.address = this.patient.homeAddress;
            this.levelOfCare.phone = this.patient.phoneNumber.number;
            this.levelOfCare.fax = this.patient.homeAddress.fax;
            this.levelOfCare.email = this.patient.emailAddress;
            this.levelOfCare.careLocationName = Object.assign({
              id: 1,
              description: "Home",
            });
          } else {
            this.referralService
              .getLocFacility(this.levelOfCareData.facilityId)
              .subscribe((x: any) => {
                this.levelOfCare.address = x.address;
                this.levelOfCare.phone = x.address.phone;
                this.levelOfCare.fax = x.address.fax;
                this.levelOfCare.email = x.address.emailAddress;
                this.levelOfCare.careLocationName = Object.assign({
                  id: this.levelOfCare.facilityId,
                  description: x.name,
                });
              });
          }
        }
        if (this.unassignedVisit.role !== "") {
          let role = this.unassignedVisit.role;
          let newRole = this.companyRoles.find(function (c) {
            return c.display === role;
          });
          this.handleRoleChange(newRole, true);
        }
        this.decreaseSpinnerCount();
      },
      (err) => {
        this.decreaseSpinnerCount();
      }
    );
  }

  getLocData(): Observable<LevelOfCareResponse> {
    return new Observable((observer) => {
      try {
        this.referralService.getLocData(this.patient.id).subscribe(
          (data: LevelOfCareResponse) => {
            observer.next(data);
            observer.complete();
          },
          (err) => {
            observer.error(err);
            observer.complete();
          }
        );
      } catch (error) {}
    });
  }

  setDateTime(val, control) {
    if (val) {
      this.scheduleForm.controls[control].setValue(val.hour + ":" + val.minute);
      if (control === "startTime") {
        this.addThirtyMinutesToEndTime(val);
      }
    } else {
      this.scheduleForm.controls[control].setValue("", { emitEvent: false });
    }
  }

  private addThirtyMinutesToEndTime(startTime): void {
    let { hour, minute } = startTime;
    minute += 30;

    if (minute >= 60) {
      hour += Math.floor(minute / 60);
      minute = minute % 60;
    }

    // Adjust the hour to be within the [0, 23] range
    if (hour >= 24) {
      hour = hour % 24;
    }

    this.endTimeValue = {
      hour: hour,
      minute: minute,
    };
    this.setDateTime({ hour: hour, minute: minute, second: 0 }, "endTime");
  }

  setDaysBeforeStartDate() {
    this.daysControlSub = this.daysControl.valueChanges.subscribe((day) => {
      if (
        (this.daysControl.valid &&
          this.unassignedVisit.timeInterval === "WEEK") ||
        this.unassignedVisit.timeInterval === "2_WEEKS"
      ) {
        const orderStartDay = new Date(
          this.unassignedVisit.orderStartDate
        ).getDay();
        const selectedDay = day.find((c) => c.selected === true);
        if (selectedDay && selectedDay.day) {
          const selectedDayNum = this.getDayFromString(selectedDay.day);
          this.daysBeforeStartDate = selectedDayNum < orderStartDay;
        } else {
          this.daysBeforeStartDate = false;
        }
      } else {
        this.daysBeforeStartDate = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.daysControlSub) {
      this.daysControlSub.unsubscribe();
    }
  }

  initializeUpcomingBenefits = () => {
    let todayDate = moment().local(true).format("YYYY-MM-DD");
    this.certificationSvc
      .GetCurrentBenefitPeriod(this.patient.externalId, todayDate)
      .subscribe(
        (benefitPeriod: BenefitPeriod) => {
          if (benefitPeriod && benefitPeriod.benefitPeriod != 0) {
            this.currentBenefitPeriod = benefitPeriod;
            this.certificationSvc
              .getUpcomingBenefitPeriod(
                this.patient.externalId,
                benefitPeriod.benefitPeriod + 1
              )
              .subscribe((nextBenefitPeriod: BenefitPeriod) => {
                if (nextBenefitPeriod && nextBenefitPeriod.benefitPeriod) {
                  this.nextBenefitPeriod = nextBenefitPeriod;
                }
              });
          }
        },
        (error) => {
          console.error(error);
        }
      );
  };

  formBenefitPeriodTxt(benefitPeriod: BenefitPeriod) {
    if (benefitPeriod.benefitPeriod <= 0) return "";
    return benefitPeriod.benefitPeriod + ": ";
  }

  /**
   * SUP-20053: whether Save is unavailable.
   *
   * <p>Deliberately does NOT include {@code disableSaveButton}. That flag comes from the older
   * credential check, which is keyed on the ASSIGNED CAREGIVER rather than the documenting user and
   * has no concept of a warning — with no credential records on file for that caregiver it simply
   * refused, which made scheduling for an aide impossible.
   *
   * <p>This surface never ran the NOVA-1806 visit-type check, so nothing replaces the removed
   * flag here — the form's own validity is the only gate.
   */
  get isSaveDisabled(): boolean {
    return !this.scheduleForm || !this.scheduleForm.valid;
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClickResource() {
    this.resourceHiddenInput.setFocus();
    this.resourceListSelect.filterInput.nativeElement.focus();
  }
}



// WEBPACK FOOTER //
// ./src/components/calendar-schedule-order/calendar-schedule-order.ts